import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  AlertColor,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import PrimaryButton from 'components/Button/PrimaryButton';
import { FormDialog } from 'components/Dialog/FormDialog';
import DrawerMenu from 'components/DrawerMenu';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import { ToolTip } from 'components/Tooltip/ToolTip';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from 'utils/axios';
import { privileges } from 'utils/constants/privileges';
import { URLS } from 'utils/constants/urls';
import { Privilege } from 'utils/redux/reducer/authentication-slice';
import { pageSubtitleSx, pageTitleSxResponsive } from 'utils/styles/pageHeader';
import { getDrawerWidth } from 'utils/validators/HelperFunctions';
import ListPageImportDrawerComponent from '../../CommonPage/ListPageImportDrawerComponent';
import { getCity, getCountry, getState } from './AddEditAddress/addressService';

type AddressEntity = { id: number | string; name: string };
type EntityKind = 'Country' | 'State' | 'City';
type DialogMode = 'add' | 'edit' | 'delete';

type AddressPanelProps = {
  title: string;
  emptyHint: string;
  items: AddressEntity[];
  selectedId: number | string | '';
  enabled: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onSelect: (id: number | string) => void;
  onAdd: () => void;
  onEdit: (item: AddressEntity) => void;
  onDelete: (item: AddressEntity) => void;
};

const AddressPanel: FC<AddressPanelProps> = ({
  title,
  emptyHint,
  items,
  selectedId,
  enabled,
  canAdd,
  canEdit,
  canDelete,
  search,
  onSearchChange,
  onSelect,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => String(item.name || '').toLowerCase().includes(q));
  }, [items, search]);

  return (
    <Paper
      elevation={0}
      sx={{
        minHeight: { xs: 360, md: 'calc(100vh - 220px)' },
        height: { xs: 420, md: 'calc(100vh - 220px)' },
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        opacity: enabled ? 1 : 0.55,
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
        }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography fontWeight={600} fontSize="0.9375rem">
            {title}
            <Typography component="span" color="text.secondary" fontSize="0.8125rem" sx={{ ml: 0.75 }}>
              ({filtered.length})
            </Typography>
          </Typography>
          <ToolTip title={canAdd && enabled ? `Add ${title.slice(0, -1)}` : `Select a parent item first`}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton
                size="small"
                color="primary"
                disabled={!canAdd || !enabled}
                onClick={onAdd}
                aria-label={`add-${title}`}
                sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                <AddOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </ToolTip>
        </Stack>
        <TextField
          size="small"
          fullWidth
          disabled={!enabled}
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={`Search ${title.toLowerCase()}`}
          sx={{ mt: 1.25 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!enabled ? (
          <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.8125rem' }}>{emptyHint}</Typography>
        ) : filtered.length === 0 ? (
          <Typography sx={{ p: 2, color: 'text.secondary', fontSize: '0.8125rem' }}>
            {items.length === 0 ? `No ${title.toLowerCase()} yet.` : 'No matches.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filtered.map((item, index) => {
              const selected = String(selectedId) === String(item.id);
              return (
                <ListItemButton
                  key={`${title}-${item.id}-${index}`}
                  selected={selected}
                  onClick={() => onSelect(item.id)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' },
                      '& .MuiIconButton-root': { color: 'primary.contrastText' },
                    },
                  }}>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: selected ? 600 : 500,
                      noWrap: true,
                    }}
                  />
                  <Stack direction="row" spacing={0.25} onClick={e => e.stopPropagation()}>
                    {canEdit && (
                      <ToolTip title="Edit">
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <IconButton size="small" onClick={() => onEdit(item)} aria-label={`edit-${item.id}`}>
                            <EditOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </ToolTip>
                    )}
                    {canDelete && (
                      <ToolTip title="Delete">
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <IconButton size="small" onClick={() => onDelete(item)} aria-label={`delete-${item.id}`}>
                            <DeleteOutlineOutlinedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      </ToolTip>
                    )}
                  </Stack>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
};

const AddressList: FC = () => {
  const theme = useTheme();
  const IsPrivilege = useSelector(Privilege);
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [countries, setCountries] = useState<AddressEntity[]>([]);
  const [states, setStates] = useState<AddressEntity[]>([]);
  const [cities, setCities] = useState<AddressEntity[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState<number | string | ''>('');
  const [selectedStateId, setSelectedStateId] = useState<number | string | ''>('');
  const [selectedCityId, setSelectedCityId] = useState<number | string | ''>('');
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('add');
  const [dialogKind, setDialogKind] = useState<EntityKind>('Country');
  const [dialogItem, setDialogItem] = useState<AddressEntity | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({ display: false, severity: null, message: '' });

  const canAdd = IsPrivilege?.includes(privileges.add_address);
  const canEdit = IsPrivilege?.includes(privileges.edit_address);
  const canDelete = IsPrivilege?.includes(privileges.delete_address);
  const canImport = IsPrivilege?.includes(privileges.import_address);

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const data = (await getCountry()) || [];
      setCountries(data);
    } catch (e: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: e?.response?.data?.message || e.message || 'Failed to load countries',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const selectCountry = useCallback(async (id: number | string) => {
    setSelectedCountryId(id);
    setSelectedStateId('');
    setSelectedCityId('');
    setStates([]);
    setCities([]);
    setStateSearch('');
    setCitySearch('');
    setLoading(true);
    try {
      setStates((await getState(Number(id))) || []);
    } catch (e: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: e?.response?.data?.message || e.message || 'Failed to load states',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const selectState = useCallback(async (id: number | string) => {
    setSelectedStateId(id);
    setSelectedCityId('');
    setCities([]);
    setCitySearch('');
    setLoading(true);
    try {
      setCities((await getCity(Number(id))) || []);
    } catch (e: any) {
      setMessage({
        display: true,
        severity: 'error',
        message: e?.response?.data?.message || e.message || 'Failed to load cities',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const openDialog = (mode: DialogMode, kind: EntityKind, item?: AddressEntity) => {
    setDialogMode(mode);
    setDialogKind(kind);
    setDialogItem(item || null);
    setInputValue(mode === 'edit' && item ? item.name : '');
    setDialogOpen(true);
  };

  const entityLabel = (kind: EntityKind) => {
    if (kind === 'Country') return strings.countryText;
    if (kind === 'State') return strings.stateText;
    return strings.cityText;
  };

  const refreshAfterChange = async (kind: EntityKind) => {
    if (kind === 'Country') {
      const data = (await getCountry()) || [];
      setCountries(data);
      if (selectedCountryId && !data.some((c: AddressEntity) => String(c.id) === String(selectedCountryId))) {
        setSelectedCountryId('');
        setSelectedStateId('');
        setSelectedCityId('');
        setStates([]);
        setCities([]);
      }
      return;
    }
    if (kind === 'State' && selectedCountryId) {
      const data = (await getState(Number(selectedCountryId))) || [];
      setStates(data);
      if (selectedStateId && !data.some((s: AddressEntity) => String(s.id) === String(selectedStateId))) {
        setSelectedStateId('');
        setSelectedCityId('');
        setCities([]);
      }
      return;
    }
    if (kind === 'City' && selectedStateId) {
      const data = (await getCity(Number(selectedStateId))) || [];
      setCities(data);
      if (selectedCityId && !data.some((c: AddressEntity) => String(c.id) === String(selectedCityId))) {
        setSelectedCityId('');
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      let url = '';
      let method: 'post' | 'put' | 'delete' = 'post';

      if (dialogMode === 'add') {
        method = 'post';
        if (dialogKind === 'Country') url = URLS.countries;
        else if (dialogKind === 'State') url = URLS.state(Number(selectedCountryId));
        else url = URLS.city(Number(selectedStateId));
      } else if (dialogMode === 'edit') {
        method = 'put';
        const id = Number(dialogItem?.id);
        if (dialogKind === 'Country') url = URLS.get_put_delete_country(id);
        else if (dialogKind === 'State') url = URLS.get_put_delete_state(id);
        else url = URLS.get_put_delete_city(id);
      } else {
        method = 'delete';
        const id = Number(dialogItem?.id);
        if (dialogKind === 'Country') url = URLS.get_put_delete_country(id);
        else if (dialogKind === 'State') url = URLS.get_put_delete_state(id);
        else url = URLS.get_put_delete_city(id);
      }

      const body =
        dialogMode === 'delete'
          ? undefined
          : { localizedAttributes: [{ language: 'en_IN', name: inputValue.trim() }] };

      const { status } = await axiosInstance[method](url, body);
      if (status === 200 || status === 204) {
        const label = entityLabel(dialogKind);
        setMessage({
          display: true,
          severity: 'success',
          message: `${label} ${strings.addressSuccessText1} ${
            dialogMode === 'add'
              ? strings.addressSuccessText2
              : dialogMode === 'edit'
                ? strings.addressSuccessText3
                : 'deleted'
          } ${strings.addressSuccessText4}`,
        });
        await refreshAfterChange(dialogKind);
        setDialogOpen(false);
        setInputValue('');
        setDialogItem(null);
      }
    } catch (error: any) {
      setMessage({
        display: true,
        severity: 'error',
        message:
          error?.response?.data?.error_description ||
          error?.response?.data?.message ||
          error.message ||
          'Request failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle =
    dialogKind === 'Country'
      ? strings.address_form_text_country
      : dialogKind === 'State'
        ? strings.address_form_text_state
        : strings.address_form_text_city;

  const selectedCountry = countries.find(c => String(c.id) === String(selectedCountryId));
  const selectedState = states.find(s => String(s.id) === String(selectedStateId));

  return (
    <Box>
      {(loading || saving) && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={() => setMessage(prev => ({ ...prev, display: false }))}
        />
      )}

      <Grid container spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm>
          <Typography sx={pageTitleSxResponsive(screenSizeDownSm)}>{strings.address_title_list}</Typography>
          <Typography sx={{ ...pageSubtitleSx, mt: 0.25 }}>{strings.address_list_subtitle}</Typography>
        </Grid>
        {canImport && (
          <Grid item xs={12} sm="auto">
            <Button
              variant="outlined"
              onClick={() => setImportOpen(true)}
              startIcon={<FileUploadOutlinedIcon />}
              sx={{ textTransform: 'capitalize' }}>
              Import
            </Button>
          </Grid>
        )}
      </Grid>

      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        alignItems="center"
        sx={{
          mb: 1.5,
          px: 1.5,
          py: 1,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'action.hover',
          minHeight: 42,
        }}>
        <Typography fontSize="0.75rem" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
          Selection
        </Typography>
        <Typography fontSize="0.875rem" fontWeight={600} color={selectedCountry ? 'text.primary' : 'text.disabled'}>
          {selectedCountry?.name || 'Country'}
        </Typography>
        <Typography fontSize="0.875rem" color="text.disabled">
          →
        </Typography>
        <Typography fontSize="0.875rem" fontWeight={600} color={selectedState ? 'text.primary' : 'text.disabled'}>
          {selectedState?.name || 'State'}
        </Typography>
        <Typography fontSize="0.875rem" color="text.disabled">
          →
        </Typography>
        <Typography fontSize="0.875rem" color="text.disabled">
          Cities
        </Typography>
      </Stack>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <AddressPanel
            title="Countries"
            emptyHint="Countries will appear here."
            items={countries}
            selectedId={selectedCountryId}
            enabled
            canAdd={!!canAdd}
            canEdit={!!canEdit}
            canDelete={!!canDelete}
            search={countrySearch}
            onSearchChange={setCountrySearch}
            onSelect={selectCountry}
            onAdd={() => openDialog('add', 'Country')}
            onEdit={item => openDialog('edit', 'Country', item)}
            onDelete={item => openDialog('delete', 'Country', item)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AddressPanel
            title="States"
            emptyHint="Select a country to view states."
            items={states}
            selectedId={selectedStateId}
            enabled={Boolean(selectedCountryId)}
            canAdd={!!canAdd && Boolean(selectedCountryId)}
            canEdit={!!canEdit}
            canDelete={!!canDelete}
            search={stateSearch}
            onSearchChange={setStateSearch}
            onSelect={selectState}
            onAdd={() => openDialog('add', 'State')}
            onEdit={item => openDialog('edit', 'State', item)}
            onDelete={item => openDialog('delete', 'State', item)}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <AddressPanel
            title="Cities"
            emptyHint="Select a state to view cities."
            items={cities}
            selectedId={selectedCityId}
            enabled={Boolean(selectedStateId)}
            canAdd={!!canAdd && Boolean(selectedStateId)}
            canEdit={!!canEdit}
            canDelete={!!canDelete}
            search={citySearch}
            onSearchChange={setCitySearch}
            onSelect={setSelectedCityId}
            onAdd={() => openDialog('add', 'City')}
            onEdit={item => openDialog('edit', 'City', item)}
            onDelete={item => openDialog('delete', 'City', item)}
          />
        </Grid>
      </Grid>

      <FormDialog
        maxWidth="xs"
        open={dialogOpen}
        closeButton
        handleClose={() => setDialogOpen(false)}
        title={dialogTitle}
        contentText=""
        showButton={dialogMode === 'delete'}
        loading={saving}
        handleSave={dialogMode === 'delete' ? handleSave : () => {}}
        children={
          <Grid>
            {dialogMode === 'delete' ? (
              <Box>
                <Typography fontWeight={500} mb={2}>
                  {strings.formatString(strings.addressConfirmDelete, dialogKind)}
                </Typography>
                {dialogKind !== 'City' && (
                  <Typography fontWeight={300} fontSize={14} color="text.secondary">
                    {strings.addressNoteText1}{' '}
                    {dialogKind === 'Country' ? strings.statesText : strings.citiesText} {strings.addressNoteText2}
                    {dialogKind}
                  </Typography>
                )}
              </Box>
            ) : (
              <>
                <TextField
                  autoFocus
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  fullWidth
                  label={dialogTitle}
                  variant="outlined"
                  size="small"
                />
                <PrimaryButton
                  disabled={!inputValue.trim()}
                  fullWidth
                  sx={{ mt: 2.5 }}
                  variant="contained"
                  onClick={handleSave}>
                  {dialogMode === 'add' ? strings.common_column_add : strings.common_column_edit}
                </PrimaryButton>
              </>
            )}
          </Grid>
        }
      />

      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={importOpen}
        handleClose={() => setImportOpen(false)}
        closeButton
        title={`${strings.usermgmt_button_import} ${strings.address_title_list}`}>
        <ListPageImportDrawerComponent
          setOpenDrawer={setImportOpen}
          isLoading={loading}
          setIsLoading={setLoading}
          setMessage={setMessage}
          downloadTempateApi={URLS.download_import_address_template}
          importApi={URLS.import_address}
        />
      </DrawerMenu>
    </Box>
  );
};

export default AddressList;
